/**
 * A tabela da aba Lançamentos, com a seleção, o selo de situação, as ações de
 * cada linha e o rodapé com o líquido.
 *
 * Papel: reproduz `#lanc-table-card` e o corpo de `renderLancamentos`
 * (l. 448–458, 3073–3128): a alça de arraste, a caixa de marcar, Data,
 * Favorecido, Descrição com a observação embaixo, Categoria na cor dela,
 * Subcategoria, Pagamento, Valor com `+` ou `-` pelo tipo, o selo de situação
 * e as ações; embaixo, o líquido de tudo o que a tabela mostra, pendentes
 * inclusive.
 *
 * Como usar: as linhas chegam na ordem da tabela, e a tabela não ordena nem
 * filtra. A seleção é de quem monta (`selecionados`, por `id`), porque ela
 * sobrevive à troca de filtro e a barra de lote soma linhas que a tabela
 * deixou de mostrar. A caixa do cabeçalho marca ou desmarca só as linhas à
 * vista, e fica marcada ou indeterminada comparando **quantas** estão
 * selecionadas com quantas estão à vista, como a origem (l. 2898–2899).
 *
 * Quem só tem `ver` não recebe a alça, o ✎, o ✓ nem o 🗑, e o selo não é
 * clicável. O ✓ e o 🗑 levam o `title` da origem também como nome acessível,
 * porque o símbolo sozinho não diz o que o botão faz. O 🧾 aparece no lançamento de grupo de ajuda de custo ou de
 * subcategoria "Ajuda de Custo", "Benefícios alimentação" ou "Diaristas Passadeiras"; o que ele faz é da parte 5.
 *
 * A cor da categoria é a gravada no plano (exceção 5), por `style`; sem cor, o
 * nome sai em `--foreground`. Cada linha leva `data-lancamento-id`, que é como
 * o arraste acha a linha sob o ponteiro.
 */
import {
  type LancamentoDaListagem,
  ROTULOS_FORMA,
  SUBCATEGORIA_DA_ALIMENTACAO,
  SUBCATEGORIA_DA_DIARISTA,
  type TipoLancamento,
} from '@concierge/shared';
import { IconClock, IconEdit, IconReceipt, IconSend } from '@tabler/icons-react';
import { type PointerEvent, useEffect, useRef } from 'react';

import { formatarData } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { formatarReais, valorDaLinha } from '../formato';
import { AlcaDeArraste } from './kit/alca-de-arraste';
import { Botao } from './kit/botao';
import { Cartao } from './kit/cartao';
import { Selo } from './kit/selo';
import {
  Celula,
  CelulaDeCabecalho,
  LinhaDaTabela,
  LinhaVazia,
  type SituacaoDaLinhaVazia,
  Tabela,
} from './kit/tabela';

/** As subcategorias que mostram o 🧾 mesmo sem grupo de ajuda de custo. */
const SUBCATEGORIAS_DO_RECIBO: readonly string[] = [
  'Ajuda de Custo',
  SUBCATEGORIA_DA_ALIMENTACAO,
  SUBCATEGORIA_DA_DIARISTA,
];

/** A cor do valor de cada tipo (l. 3075). */
const COR_DO_VALOR: Readonly<Record<TipoLancamento, string>> = {
  receita: 'text-sucesso',
  credito_movimentacao: 'text-sucesso',
  despesa: 'text-perigo',
  debito_movimentacao: 'text-emb-azul',
};

interface TabelaDeLancamentosProps {
  linhas: readonly LancamentoDaListagem[];
  /** O líquido do rodapé, que a API soma sobre as mesmas linhas. */
  soma: number;
  /** `pronta` desenha as linhas; as outras ocupam a tabela com a linha da base. */
  situacao: 'pronta' | Exclude<SituacaoDaLinhaVazia, 'vazia'>;
  onTentarDeNovo: () => void;
  /** Os selecionados, por `id`, à vista ou não. */
  selecionados: ReadonlyMap<string, LancamentoDaListagem>;
  /** Marca ou desmarca uma linha. */
  onMarcar: (linha: LancamentoDaListagem, marcada: boolean) => void;
  /** Marca ou desmarca as linhas à vista, pela caixa do cabeçalho. */
  onMarcarVisiveis: (marcadas: boolean) => void;
  /** A cor gravada de cada categoria do plano. */
  coresDasCategorias: ReadonlyMap<string, string | null>;
  /** Falso para quem só tem `ver`. */
  podeEditar: boolean;
  onEditar: (linha: LancamentoDaListagem) => void;
  onConfirmarPago: (linha: LancamentoDaListagem) => void;
  onExcluir: (linha: LancamentoDaListagem) => void;
  /** O clique no selo de pendente, que troca a provisão. */
  onAlternarProvisao: (linha: LancamentoDaListagem) => void;
  /** O 🧾, da parte 5. */
  onImprimirRecibo?: (linha: LancamentoDaListagem) => void;
  /** O aperto da alça, que começa o arraste. */
  onAgarrar?: (evento: PointerEvent<HTMLSpanElement>, linha: LancamentoDaListagem) => void;
  /** A linha que está sendo arrastada, a 40 %. */
  idArrastado?: string | null;
  /** A linha sob o ponteiro durante o arraste. */
  idSobre?: string | null;
}

/**
 * A caixa de marcar do cabeçalho, com o estado indeterminado.
 *
 * @param marcada - Se está marcada.
 * @param indeterminada - Se está no meio, com seleção parcial.
 * @param onMudar - Recebe a nova marcação.
 * @returns A caixa.
 */
function CaixaDeTodas({
  marcada,
  indeterminada,
  onMudar,
}: {
  marcada: boolean;
  indeterminada: boolean;
  onMudar: (marcada: boolean) => void;
}) {
  const caixa = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (caixa.current !== null) caixa.current.indeterminate = indeterminada;
  }, [indeterminada]);
  return (
    <input
      ref={caixa}
      type="checkbox"
      aria-label="Selecionar todos os lançamentos à vista"
      checked={marcada}
      onChange={(evento) => onMudar(evento.target.checked)}
      className="cursor-pointer"
    />
  );
}

/**
 * O selo de situação de uma linha.
 *
 * @param linha - O lançamento.
 * @param onAlternar - Troca a provisão; ausente, o selo não é clicável.
 * @returns O selo.
 */
function SeloDaLinha({
  linha,
  onAlternar,
}: {
  linha: LancamentoDaListagem;
  onAlternar: (() => void) | undefined;
}) {
  if (linha.status === 'pago') return <Selo cor="verde">✓ Pago</Selo>;
  if (linha.status === 'provisionado') {
    return (
      <Selo
        cor="amarelo"
        className="gap-1"
        title="Clique para marcar como Lançado"
        onClick={onAlternar}
      >
        <IconClock size={12} aria-hidden="true" /> Provisionado
      </Selo>
    );
  }
  return (
    <Selo
      cor="lancado"
      className="gap-1"
      title="Clique para marcar como Provisionado"
      onClick={onAlternar}
    >
      <IconSend size={12} aria-hidden="true" /> Lançado
    </Selo>
  );
}

/**
 * A tabela de lançamentos.
 *
 * @param props - As linhas, a seleção, as permissões e os manipuladores; ver
 *   `TabelaDeLancamentosProps`.
 * @returns O cartão com a tabela.
 */
export function TabelaDeLancamentos({
  linhas,
  soma,
  situacao,
  onTentarDeNovo,
  selecionados,
  onMarcar,
  onMarcarVisiveis,
  coresDasCategorias,
  podeEditar,
  onEditar,
  onConfirmarPago,
  onExcluir,
  onAlternarProvisao,
  onImprimirRecibo,
  onAgarrar,
  idArrastado = null,
  idSobre = null,
}: TabelaDeLancamentosProps) {
  const quantos = selecionados.size;
  const comLinhas = situacao === 'pronta' && linhas.length > 0;
  const somaNegativa = Math.round(soma * 100) < 0;

  return (
    <Cartao>
      <div className="overflow-x-auto">
        <Tabela>
          <thead>
            <tr>
              <CelulaDeCabecalho
                className="w-5"
                title="Arraste as linhas para reordenar (dentro do mesmo mês)"
              />
              <CelulaDeCabecalho className="w-8">
                <CaixaDeTodas
                  marcada={quantos > 0 && quantos === linhas.length}
                  indeterminada={quantos > 0 && quantos < linhas.length}
                  onMudar={onMarcarVisiveis}
                />
              </CelulaDeCabecalho>
              <CelulaDeCabecalho>Data</CelulaDeCabecalho>
              <CelulaDeCabecalho>Favorecido</CelulaDeCabecalho>
              <CelulaDeCabecalho>Descrição</CelulaDeCabecalho>
              <CelulaDeCabecalho>Categoria</CelulaDeCabecalho>
              <CelulaDeCabecalho>Subcategoria</CelulaDeCabecalho>
              <CelulaDeCabecalho>Pagamento</CelulaDeCabecalho>
              <CelulaDeCabecalho alinhamento="direita">Valor</CelulaDeCabecalho>
              <CelulaDeCabecalho>Status</CelulaDeCabecalho>
              <CelulaDeCabecalho />
            </tr>
          </thead>
          <tbody>
            {situacao !== 'pronta' ? (
              <LinhaVazia
                colunas={11}
                situacao={situacao}
                textoDoVazio="Nenhum lançamento encontrado."
                onTentarDeNovo={onTentarDeNovo}
              />
            ) : linhas.length === 0 ? (
              <LinhaVazia
                colunas={11}
                situacao="vazia"
                textoDoVazio="Nenhum lançamento encontrado."
                onTentarDeNovo={onTentarDeNovo}
              />
            ) : (
              linhas.map((linha) => {
                const editavel = podeEditar && !linha.pago;
                const temRecibo =
                  linha.grupoAjudaCustoId !== null ||
                  SUBCATEGORIAS_DO_RECIBO.includes(linha.subcategoria ?? '');
                return (
                  <LinhaDaTabela
                    key={linha.id}
                    data-lancamento-id={linha.id}
                    className={cn(
                      linha.id === idArrastado && 'bg-superficie-2 opacity-40',
                      linha.id === idSobre && '[&>td]:!bg-emb-arraste',
                    )}
                  >
                    <Celula className="w-5 py-2 pr-1 pl-2">
                      {podeEditar && (
                        <AlcaDeArraste
                          onPointerDown={(evento) => onAgarrar?.(evento, linha)}
                        />
                      )}
                    </Celula>
                    <Celula className="w-8 px-2.5 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Selecionar ${linha.descricao}`}
                        checked={selecionados.has(linha.id)}
                        onChange={(evento) => onMarcar(linha, evento.target.checked)}
                        className="cursor-pointer"
                      />
                    </Celula>
                    <Celula className="font-outfit text-[12px] font-light whitespace-nowrap">
                      {formatarData(linha.data)}
                    </Celula>
                    <Celula className="font-outfit text-[12px] font-light whitespace-nowrap">
                      {linha.favorecido?.nome ?? ''}
                    </Celula>
                    <Celula className="font-outfit text-[12px] font-light whitespace-nowrap">
                      <div>{linha.descricao}</div>
                      {linha.observacao !== null && linha.observacao !== '' && (
                        <div className="text-[11px] text-texto-fraco">{linha.observacao}</div>
                      )}
                    </Celula>
                    <Celula className="whitespace-nowrap">
                      <span
                        className="font-outfit text-[12px] font-light"
                        style={{
                          color:
                            coresDasCategorias.get(linha.categoria) ?? 'var(--foreground)',
                        }}
                      >
                        {linha.categoria}
                      </span>
                    </Celula>
                    <Celula tipo="fraca" className="whitespace-nowrap">
                      {linha.subcategoria ?? ''}
                    </Celula>
                    <Celula tipo="fraca" className="whitespace-nowrap">
                      {linha.formaPagamento === null
                        ? ''
                        : ROTULOS_FORMA[linha.formaPagamento]}
                    </Celula>
                    <Celula
                      tipo="numero"
                      className={cn(
                        'min-w-[100px] font-outfit font-semibold',
                        COR_DO_VALOR[linha.tipo],
                      )}
                    >
                      {valorDaLinha(linha)}
                    </Celula>
                    <Celula className="whitespace-nowrap">
                      <SeloDaLinha
                        linha={linha}
                        onAlternar={podeEditar ? () => onAlternarProvisao(linha) : undefined}
                      />
                    </Celula>
                    <Celula className="whitespace-nowrap">
                      {editavel ? (
                        <Botao
                          variante="fantasma"
                          tamanho="sm"
                          icone
                          title="Editar"
                          onClick={() => onEditar(linha)}
                        >
                          <IconEdit size={15} aria-hidden="true" />
                        </Botao>
                      ) : (
                        <span className="inline-block w-[30px]" />
                      )}
                      {editavel && (
                        <Botao
                          variante="sucesso"
                          tamanho="sm"
                          icone
                          title="Confirmar pago"
                          aria-label="Confirmar pago"
                          onClick={() => onConfirmarPago(linha)}
                        >
                          ✓
                        </Botao>
                      )}
                      {temRecibo && (
                        <Botao
                          variante="fantasma"
                          tamanho="sm"
                          icone
                          title="Imprimir recibo"
                          onClick={() => onImprimirRecibo?.(linha)}
                        >
                          <IconReceipt size={15} aria-hidden="true" />
                        </Botao>
                      )}
                      {editavel && (
                        <Botao
                          variante="perigo"
                          tamanho="sm"
                          icone
                          title="Excluir lançamento"
                          aria-label="Excluir lançamento"
                          onClick={() => onExcluir(linha)}
                        >
                          🗑
                        </Botao>
                      )}
                    </Celula>
                  </LinhaDaTabela>
                );
              })
            )}
          </tbody>
          {comLinhas && (
            <tfoot>
              <tr className="border-t-2 border-emb-borda bg-superficie-2 font-bold">
                <td colSpan={8} className="px-3 py-[9px]" />
                <td
                  colSpan={2}
                  className={cn(
                    'px-3 py-[9px] text-right text-[13px] whitespace-nowrap tabular-nums',
                    somaNegativa ? 'text-perigo' : 'text-sucesso',
                  )}
                >
                  {somaNegativa ? '' : '+'}
                  {formatarReais(soma)}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </Tabela>
      </div>
    </Cartao>
  );
}
